/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect } from "react";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import { useUI } from "../theme/ThemeProvider";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import { clearSubscriptionMarkers, isPlanExpired } from "../utils/subscriptionPlan";

export default function WhatsLayout({ title, children, hideNavbar = false }) {
  const { theme, lang } = useUI();
  const [isDesktop, setIsDesktop] = useState(window.innerWidth >= 1024);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isDark = theme === "dark";
  const navigate = useNavigate();
  const location = useLocation();
  const isArabic = lang === "ar";

  useEffect(() => {
    // التحقق من صلاحية الاشتراك/التجربة بشكل دوري عند تحميل أي صفحة تستخدم هذا الـ Layout
    const checkExpiration = () => {
      const trialAlertKey = "trialExpiredAlertShown";
      const paidAlertKey = "paidExpiredAlertShown";
      const startStr = localStorage.getItem("subscriptionStartDate") || localStorage.getItem("paidStartDate") || localStorage.getItem("trialStartDate");
      const durationDays = Number(localStorage.getItem("subscriptionDurationDays") || "0");
      const rawPrice = (localStorage.getItem("selectedPlanPrice") || "0").toString().toLowerCase();
      const planTypeText = `${localStorage.getItem("selectedPlanType") || ""} ${localStorage.getItem("selectedEngPlanType") || ""}`.toLowerCase();
      const isFreeOrTrial = rawPrice === "free" || rawPrice === "0" || localStorage.getItem("trialUsed") === "1";
      
      const now = new Date();

      const computedDays =
        durationDays > 0 ? durationDays :
        planTypeText.includes("week") || planTypeText.includes("trial") ? 7 :
        planTypeText.includes("month") ? 30 :
        isFreeOrTrial ? 7 : 30;

      const isAllowedPath = 
        location.pathname.includes("/subscriptions") || 
        location.pathname.includes("/payment") || 
        location.pathname.includes("/theme-plan") ||
        location.pathname.includes("/reset-password") ||
        location.pathname.includes("/403");

      // 1. الأولوية القصوى لتاريخ الانتهاء الصريح (المخزن من صفحة الاشتراك أو الدفع)
      const explicitExpiry = localStorage.getItem("plan_expiry_date");
      if (explicitExpiry && explicitExpiry !== "undefined") {
        const expiryDate = new Date(explicitExpiry);
        if (!Number.isNaN(expiryDate.getTime())) {
          if (expiryDate > now) {
            // الاشتراك لا يزال سارياً، لا تفعل شيئاً
            return false;
          }
        }
      }

      // 2. التحقق من وجود "علامة تجديد" حديثة (لمدة ساعة مثلاً) لمنع الطرد الفوري
      const renewedAt = localStorage.getItem("plan_renewed_at");
      if (renewedAt) {
        const renewedDate = new Date(renewedAt);
        if (!Number.isNaN(renewedDate.getTime())) {
          const oneHour = 60 * 60 * 1000;
          if (now - renewedDate < oneHour) {
            return false; // نعتبره غير منتهٍ لفترة سماح بعد التجديد
          }
        }
      }

      if (startStr) {
        const startDate = new Date(startStr);
        if (!Number.isNaN(startDate.getTime())) {
          const expiredByStoredDates = Math.floor((now - startDate) / (1000 * 60 * 60 * 24)) >= computedDays;
          const expiredByPlanData = isPlanExpired({
            planType: localStorage.getItem("selectedPlanType") || "",
            engPlanType: localStorage.getItem("selectedEngPlanType") || "",
            creationDate: startStr,
          });

          if (expiredByStoredDates || expiredByPlanData) {
            const alertKey = isFreeOrTrial ? trialAlertKey : paidAlertKey;
            
            if (!sessionStorage.getItem(alertKey)) {
              sessionStorage.setItem(alertKey, "1");
              Swal.fire({
                icon: "warning",
                title: isArabic ? "انتهت الخطة" : "Plan Expired",
                text: isArabic
                  ? (isFreeOrTrial
                    ? "انتهت مدة التجربة/الخطة المجانية. يرجى الاشتراك في باقة لمتابعة العمل."
                    : "انتهت مدة الاشتراك. يرجى تجديد الاشتراك للمتابعة.")
                  : (isFreeOrTrial
                    ? "Your free trial/plan has expired. Please subscribe to continue."
                    : "Your subscription has expired. Please renew your plan to continue."),
                confirmButtonText: isArabic ? "حسنًا" : "OK",
                confirmButtonColor: "#7400BF",
                background: isDark ? "#1E293B" : "#fff",
                color: isDark ? "#fff" : "#000",
              });
            }

            if (!isAllowedPath) {
              navigate("/theme-plan");
              return true;
            }
            return false;
          }
        }
      }

      // fallback: if free/trial and no start date set, check tenant creation date
      if (isFreeOrTrial && !localStorage.getItem("trialStartDate")) {
        try {
          const tenant = JSON.parse(localStorage.getItem("tenant") || "{}");
          const tenantStart = tenant.CreatedAt || tenant.createdAt;
          if (tenantStart) {
            const fallbackStart = new Date(tenantStart);
            if (!Number.isNaN(fallbackStart.getTime())) {
              const diffDays = Math.floor((now - fallbackStart) / (1000 * 60 * 60 * 24));
              if (diffDays >= computedDays) {
                // إذا كان قد قام بالتجديد للتو، لا نعتبره منتهياً بالاعتماد على تاريخ إنشاء الحساب
                if (renewedAt) return false;

                if (!sessionStorage.getItem(trialAlertKey)) {
                  sessionStorage.setItem(trialAlertKey, "1");
                  Swal.fire({
                    icon: "warning",
                    title: isArabic ? "انتهت الخطة" : "Plan Expired",
                    text: isArabic
                      ? "انتهت مدة التجربة/الخطة المجانية. يرجى الاشتراك في باقة لمتابعة العمل."
                      : "Your free trial/plan has expired. Please subscribe to continue.",
                    confirmButtonText: isArabic ? "حسنًا" : "OK",
                    confirmButtonColor: "#7400BF",
                    background: isDark ? "#1E293B" : "#fff",
                    color: isDark ? "#fff" : "#000",
                  });
                }
                
                if (!isAllowedPath) {
                  navigate("/theme-plan");
                  return true;
                }
                return false;
              }
            }
          }
        } catch (e) {}
      }

      return false;
    };

    checkExpiration();
  }, [navigate, isArabic, location.pathname]);

  useEffect(() => {
    const handleResize = () => {
      const desktop = window.innerWidth >= 1024;
      setIsDesktop(desktop);
      if (desktop) setSidebarOpen(false); // Close mobile sidebar when resizing to desktop
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Prevent body scroll when sidebar is open on mobile
  useEffect(() => {
    if (!isDesktop && sidebarOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [sidebarOpen, isDesktop]);

  return (
    <div
      dir={lang === "ar" ? "rtl" : "ltr"}
      className={`flex min-h-screen w-full ${isDark ? "bg-[#0B1020] text-gray-100" : "bg-[#F9FAFB] text-gray-900"
        }`}
      style={{
        position: "relative",
        height: "100vh",
        overflow: "hidden",
      }}
    >
      {/* ===== Overlay للشاشات الصغيرة ===== */}
      {!isDesktop && sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998] transition-opacity duration-300 animate-in fade-in"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ===== Sidebar ===== */}
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        isAlwaysVisible={isDesktop}
      />

      {/* ===== المحتوى الرئيسي ===== */}
      <div
        className={`flex flex-col flex-1 transition-all duration-500 ease-in-out ${isDesktop
            ? lang === "ar"
              ? "mr-64"
              : "ml-64"
            : ""
          }`}
        style={{
          width: "100%",
          minWidth: 0,
          height: "100vh",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {!hideNavbar && (
          <Navbar
            onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
            isSidebarOpen={sidebarOpen}
          />
        )}

        <main
          className="flex-1 w-full px-3 sm:px-6 lg:px-8 py-4 sm:py-6"
          style={{
            overflowY: "auto",
            overflowX: "hidden",
            height: "100%",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {title && (
            <h2
              className={`text-xl sm:text-2xl font-extrabold mb-4 sm:mb-6 tracking-tight ${isDark ? "text-[#F2B441]" : "text-[#21003D]"
                }`}
            >
              {title}
            </h2>
          )}

          {/* منطقة المحتوى */}
          <div
            className="w-full flex-1"
            style={{
              paddingBottom: "80px", // Reduced padding for mobile
            }}
          >
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
